import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const handleForgot = async () => {
    setFormError('');
    if (!email) {
      setFormError('Type your email first, then click Forgot password.');
      toast({ title: 'Enter your email', description: 'Type your email above first.', variant: 'destructive' });
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      setFormError(error.message);
      toast({ title: 'Could not send', description: error.message, variant: 'destructive' });
      return;
    }
    setFormError('Password reset link sent. Check your email inbox.');
    toast({ title: 'Check your email', description: 'We sent you a password reset link.' });
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate('/admin/brochures', { replace: true });
    });
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setLoading(true);
    if (mode === 'signup') {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: `${window.location.origin}/admin/brochures` },
      });
      setLoading(false);
      if (error) {
        setFormError(error.message);
        toast({ title: 'Sign up failed', description: error.message, variant: 'destructive' });
        return;
      }
      setFormError('Account created. Check your email, then request admin access.');
      toast({
        title: 'Account created',
        description: 'Send your email to the site owner to be granted admin access.',
      });
      setMode('login');
      return;
    }
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      const message = error.message.includes('Invalid login credentials')
        ? 'Wrong email or password. If you recently reset it, use the latest password or click Forgot password.'
        : error.message;
      setFormError(message);
      toast({ title: 'Login failed', description: error.message, variant: 'destructive' });
      return;
    }
    navigate('/admin/brochures', { replace: true });
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <form onSubmit={handleSubmit} className="w-full max-w-md bg-gray-50 border border-gray-200 rounded-lg p-8 space-y-4">
        <h1 className="text-2xl font-bold text-foreground">{mode === 'login' ? 'Admin Login' : 'Create Admin Account'}</h1>
        <p className="text-sm text-muted-foreground">
          {mode === 'login' ? 'Sign in to manage brochures.' : 'Create your account, then request admin access.'}
        </p>
        {formError && (
          <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {formError}
          </p>
        )}
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} required />
        </div>
        <Button type="submit" disabled={loading} className="w-full">
          {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Sign Up'}
        </Button>
        <button
          type="button"
          onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
          className="text-sm text-primary hover:underline w-full text-center"
        >
          {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
        </button>
        {mode === 'login' && (
          <button
            type="button"
            onClick={handleForgot}
            className="text-sm text-muted-foreground hover:underline w-full text-center"
          >
            Forgot password?
          </button>
        )}
      </form>
    </div>
  );
};

export default AdminLogin;
